<script lang="ts" setup>
  import { computed } from 'vue';
  import useSlowRequestsStore from '@/common/store/pinia/slowRequests';
  import { humanizeMs, toIsoFormat } from '@/common/utils/formatter.date';
  import { useI18n } from '@/common/composables/vue-i18n.composable';
  import useThemeStore from '@/common/store/pinia/theme';
  import useTelemetryStore from '@/common/store/pinia/telemetry';

  const slowRequestsStore = useSlowRequestsStore();
  const { tc, t } = useI18n();
  const telemetryStore = useTelemetryStore();
  const themeStore = useThemeStore();

  const translations = computed(() => ({
    getDetectionsText: (count: number) => tc('Count of detections: (logged {count} slow response since first problem detection)|Count of detections: (logged {count} slow responses since first problem detection)', count, { count }),
    getAverageDelayText: () => t('Average delay'),
    getFirstSeenText: () => t('First seen'),
    getRejectedText: (count: number) => tc('Rejected {count} times with message|Rejected {count} times with messages', count, { count })
  }));

  const formatGroupedRequests = computed((): string => {
    const grouped = slowRequestsStore.groupedSlowRequests;

    if (grouped.length === 0) {
      return '';
    }

    return grouped.map((group) => {
      const lines: string[] = [];
      lines.push(`${group.method}: ${group.url}`);
      lines.push(` - ${translations.value.getDetectionsText(group.count)}`);
      lines.push(` - ${translations.value.getAverageDelayText()}: ${humanizeMs(group.averageDelay, true)}`);
      lines.push(` - ${translations.value.getFirstSeenText()}: ${toIsoFormat(group.firstSeen)}`);

      if (group.errors.length > 0) {
        const totalErrors = group.errors.reduce((sum, err) => sum + err.count, 0);
        lines.push(` - ${translations.value.getRejectedText(totalErrors)}:`);
        group.errors.forEach((error) => {
          lines.push(`   - ${error.message}`);
        });
      }

      return lines.join('\n');
    }).join('\n\n');
  });
</script>

<template>
  <div class="slow-requests-details v-slow-requests-details">
    <el-alert
      class="ma-24"
      hide-close
      card
    >
      <template #description>
        <ul class="ma-0 pl-0 slow-requests-details-list">
          <li>
            {{ $t('Please contact support if the operation does not complete.') }}
          </li>
          <li v-if="!themeStore.themeData.useTelemetry && !telemetryStore.enabled">
            {{ $t('Enable telemetry in the settings so that we can automatically analyse such issues.') }}
          </li>
        </ul>
      </template>
    </el-alert>
    <pre
      v-if="formatGroupedRequests"
      class="grouped-requests-code ma-24 px-16"
    >
      <code>{{ formatGroupedRequests }}</code>
    </pre>
  </div>
</template>

<style lang="scss" scoped>
  .grouped-requests-code {
    background: $av-brand-lightest;
    border: 1px solid $av-brand-light;
    border-radius: 4px;
    overflow-x: auto;

    code {
      display: block;
      white-space: pre-wrap;
      padding: 0;
      font-size: $av-font-size-xs;
      line-height: 1.5;
    }
  }

  .slow-requests-details-list {
    list-style: none;
  }
</style>
